import { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Check, Zap, Star, ArrowUpRight,
  Download, CheckCircle, RefreshCw, Loader2,
  AlertCircle, ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSTNAuth } from '@/context/STNAuthContext';
import { useNotifications } from '@/context/NotificationContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  getPlans, getMySubscription, getInvoices,
  createBillingOrder, verifyBillingPayment, switchToFreePlan,
} from '@/api/stnApi';

// ── Load Razorpay script once ─────────────────────────────────────────────────
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// ── Badge styles ──────────────────────────────────────────────────────────────
const BADGE_MAP = {
  zinc:    'bg-muted text-muted-foreground border-border',
  violet:  'bg-primary/10 text-primary border-primary/20',
  gold:    'bg-warning/10 text-warning border-warning/20',
  emerald: 'bg-success/10 text-success border-success/20',
  rose:    'bg-destructive/10 text-destructive border-destructive/20',
};
const planBadge = (color) => BADGE_MAP[color] || BADGE_MAP.zinc;

function fmtLimit(n) { return n === -1 ? 'Unlimited' : String(n); }

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtAmount(n) {
  if (n === 0) return '₹0';
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

// ── Plan Card ─────────────────────────────────────────────────────────────────
function PlanCard({ plan, currentSlug, billing, onSelect, selecting }) {
  const isCurrent = plan.slug === currentSlug;
  const price = billing === 'yearly' && plan.yearlyDiscount > 0
    ? Math.round(plan.price * 12 * (1 - plan.yearlyDiscount / 100) / 12)
    : plan.price;
  const isFree = plan.price === 0;

  return (
    <Card className={cn(
      'flex flex-col relative overflow-hidden border transition-all',
      isCurrent ? 'border-primary shadow-glow' : 'border-border hover:border-primary/40',
      plan.isPopular && !isCurrent ? 'ring-1 ring-primary/30' : ''
    )}>
      {isCurrent && <div className="absolute top-0 left-0 right-0 h-0.5 gradient-primary" />}
      {plan.isPopular && (
        <div className="absolute top-3 right-3">
          <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full border', planBadge(plan.badgeColor || 'violet'))}>
            Most Popular
          </span>
        </div>
      )}
      <CardContent className="p-6 flex flex-col h-full">
        <div className="mb-1">
          <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full border', planBadge(plan.badgeColor || 'zinc'))}>
            {plan.name}
          </span>
        </div>
        {plan.tagline && (
          <p className="text-xs text-muted-foreground mt-1.5 mb-3 leading-snug">{plan.tagline}</p>
        )}
        <div className="flex items-baseline gap-1 mb-1">
          <span className="text-3xl font-bold text-foreground">
            {isFree ? '₹0' : `₹${price.toLocaleString('en-IN')}`}
          </span>
          <span className="text-muted-foreground text-sm">/month</span>
        </div>
        {billing === 'yearly' && !isFree && plan.yearlyDiscount > 0 && (
          <p className="text-xs text-success mb-4">
            ₹{Math.round(plan.price * 12 * (1 - plan.yearlyDiscount / 100)).toLocaleString('en-IN')}/yr — save {plan.yearlyDiscount}%
          </p>
        )}
        <ul className="space-y-2 flex-1 mb-6 mt-2">
          {(plan.features || []).map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
              <span className="text-sm text-muted-foreground">{f}</span>
            </li>
          ))}
        </ul>
        <Button
          className={cn('w-full mt-auto',
            isCurrent ? 'border-border text-muted-foreground cursor-default' :
            'gradient-primary text-primary-foreground border-0 hover:opacity-90'
          )}
          variant={isCurrent ? 'outline' : 'default'}
          onClick={() => !isCurrent && onSelect(plan)}
          disabled={isCurrent || (selecting === plan.slug)}
        >
          {selecting === plan.slug ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Processing…
            </span>
          ) : isCurrent ? 'Current Plan' : isFree ? 'Switch to Free' : `Upgrade to ${plan.name}`}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Invoice row ───────────────────────────────────────────────────────────────
function InvoiceRow({ inv }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border border-border">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-chart-3/10 flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-chart-3" />
        </div>
        <div>
          <p className="font-medium text-sm text-foreground">
            {inv.planName || inv.description || 'Subscription'}
          </p>
          <p className="text-xs text-muted-foreground">
            {fmtDate(inv.createdAt)} • {inv.invoiceNumber || '—'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-semibold text-foreground">{fmtAmount(inv.amount)}</span>
        <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium border',
          inv.status === 'paid' ? 'bg-success/10 text-success border-success/20' : 'bg-warning/10 text-warning border-warning/20'
        )}>
          {inv.status || 'paid'}
        </span>
        {inv.downloadUrl && (
          <Button variant="ghost" size="sm" onClick={() => window.open(inv.downloadUrl, '_blank')}>
            <Download className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Billing() {
  const { founder } = useSTNAuth();
  const { notifications } = useNotifications?.() ?? {};

  const [plans,        setPlans]        = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices,     setInvoices]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [billing,      setBilling]      = useState('monthly');
  const [selecting,    setSelecting]    = useState(null);

  const currentSlug = subscription?.planSlug || 'free';
  const gncBalance  = 0; // pulled from gamification wallet if needed

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [plansData, subData] = await Promise.all([getPlans(), getMySubscription()]);
      setPlans(Array.isArray(plansData) ? plansData : (plansData?.data ?? []));
      setSubscription(subData?.data ?? subData ?? null);
    } catch (e) {
      toast.error(e.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setInvoiceLoading(true);
    try {
      const data = await getInvoices({ page: 1, limit: 30 });
      setInvoices(data?.data ?? []);
    } catch { /* silent */ }
    finally { setInvoiceLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSelectPlan = async (plan) => {
    if (plan.price === 0) {
      // Switch to free
      try {
        setSelecting('free');
        await switchToFreePlan();
        toast.success('Switched to Free plan');
        fetchAll();
      } catch (e) {
        toast.error(e.message || 'Failed to switch plan');
      } finally {
        setSelecting(null);
      }
      return;
    }

    // Paid plan — Razorpay
    const loaded = await loadRazorpayScript();
    if (!loaded) { toast.error('Failed to load payment gateway'); return; }

    try {
      setSelecting(plan.slug);
      const orderData = await createBillingOrder({ planSlug: plan.slug, billing });
      const order = orderData?.data ?? orderData;

      const options = {
        key:         order.keyId,
        amount:      order.amount,
        currency:    order.currency || 'INR',
        name:        'StartupNexus',
        description: `${plan.name} Plan — ${billing}`,
        ...(order.type === 'subscription'
          ? { subscription_id: order.subscriptionId }
          : { order_id: order.orderId }
        ),
        prefill: {
          name:  founder?.name  || '',
          email: founder?.email || '',
        },
        theme: { color: '#7c3aed' },
        handler: async (response) => {
          try {
            await verifyBillingPayment({
              razorpay_order_id:        response.razorpay_order_id,
              razorpay_payment_id:      response.razorpay_payment_id,
              razorpay_signature:       response.razorpay_signature,
              razorpay_subscription_id: response.razorpay_subscription_id,
              planSlug:        plan.slug,
              billing,
              originalAmount:  order.originalAmount,
              finalAmount:     order.finalAmount,
            });
            toast.success(`${plan.name} plan activated!`);
            fetchAll();
            fetchInvoices();
          } catch (e) {
            toast.error(e.message || 'Payment verification failed');
          } finally {
            setSelecting(null);
          }
        },
        modal: {
          ondismiss: () => { setSelecting(null); },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.message || 'Failed to initiate payment');
      setSelecting(null);
    }
  };

  const sub = subscription;
  const activePlan = plans.find(p => p.slug === currentSlug);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Billing & Plans</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage your subscription, payments, and invoices</p>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAll} disabled={loading}>
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
        </Button>
      </div>

      {/* Current plan banner */}
      {loading ? (
        <Card className="border-border animate-pulse">
          <CardContent className="p-6 h-20" />
        </Card>
      ) : (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center">
                  <Zap className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-bold text-foreground capitalize">{currentSlug} Plan</h2>
                    <span className="badge-success">Active</span>
                    {sub?.billingCycle && sub.billingCycle !== 'free' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border capitalize">
                        {sub.billingCycle}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {currentSlug === 'free'
                      ? 'Free forever — no credit card required'
                      : sub?.expiresAt
                        ? `Renews on ${fmtDate(sub.expiresAt)} — ${fmtAmount(sub.plan?.price || activePlan?.price || 0)}/month`
                        : 'Active subscription'}
                  </p>
                </div>
              </div>
              {currentSlug !== 'pro' && plans.length > 0 && (
                <Button
                  className="gradient-primary text-primary-foreground border-0"
                  onClick={() => {
                    const nextPlan = plans.find(p =>
                      p.slug === (currentSlug === 'free' ? 'plus' : 'pro')
                    );
                    if (nextPlan) handleSelectPlan(nextPlan);
                  }}
                  disabled={!!selecting}
                >
                  Upgrade Plan <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="plans">
        <TabsList className="bg-muted">
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="history" onClick={fetchInvoices}>Payment History</TabsTrigger>
          <TabsTrigger value="coins">NexusCoins</TabsTrigger>
        </TabsList>

        {/* Plans tab */}
        <TabsContent value="plans" className="mt-6">
          {/* Billing cycle toggle */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              {[
                { key: 'monthly', label: 'Monthly' },
                { key: 'yearly',  label: 'Yearly' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setBilling(key)}
                  className={cn(
                    'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                    billing === key
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {billing === 'yearly' && (
              <span className="text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded-full border border-success/20">
                Save up to 25% with annual
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <Card key={i} className="border-border animate-pulse">
                  <CardContent className="p-6 h-64" />
                </Card>
              ))}
            </div>
          ) : plans.length === 0 ? (
            <Card className="border-border">
              <CardContent className="p-12 text-center">
                <AlertCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                <p className="text-muted-foreground text-sm">No plans configured yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...plans].sort((a, b) => a.order - b.order).map(plan => (
                <PlanCard
                  key={plan._id || plan.slug}
                  plan={plan}
                  currentSlug={currentSlug}
                  billing={billing}
                  onSelect={handleSelectPlan}
                  selecting={selecting}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Payment History tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Payment History</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchInvoices} disabled={invoiceLoading}>
                <RefreshCw className={cn('w-3.5 h-3.5', invoiceLoading && 'animate-spin')} />
              </Button>
            </CardHeader>
            <CardContent>
              {invoiceLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                  <p className="text-muted-foreground text-sm">No payments yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Upgrade to a paid plan to see your invoices here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map((inv, i) => (
                    <InvoiceRow key={inv._id || i} inv={inv} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* NexusCoins tab */}
        <TabsContent value="coins" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                    <Star className="w-6 h-6 text-warning" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">NexusCoins (GNC)</h3>
                    <p className="text-xs text-muted-foreground">Earned via STNXP conversion</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-foreground mb-1">
                  {gncBalance} <span className="text-base text-muted-foreground font-normal">coins</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  NexusCoins can be used for premium matching boosts, featured employer listings, and priority event placement.
                </p>
                <div className="mt-4 p-3 rounded-xl bg-muted/50 border border-border">
                  <p className="text-xs text-muted-foreground">
                    Earn STNXP by hiring interns, hosting events, and more — then convert to GNC on the{' '}
                    <a href="/gamification" className="text-primary underline-offset-2 hover:underline">Gamification</a> page.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">Plan Benefits</h3>
                    <p className="text-xs text-muted-foreground capitalize">{currentSlug} plan active</p>
                  </div>
                </div>
                {activePlan ? (
                  <ul className="space-y-2">
                    {[
                      ['Intern Postings', activePlan.internPostings],
                      ['Interns / Month', activePlan.internsPerMonth],
                      ['Pool Views / Day', activePlan.poolViewsPerDay],
                      ['Team Members', activePlan.teamMembers],
                      ['Events / Month', activePlan.eventsPerMonth],
                    ].map(([label, val]) => (
                      <li key={label} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-semibold text-foreground">{fmtLimit(val)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">Loading plan details…</p>
                )}
                {currentSlug === 'free' && (
                  <Button
                    className="w-full mt-4 gradient-primary text-primary-foreground border-0"
                    onClick={() => {
                      const plus = plans.find(p => p.slug === 'plus');
                      if (plus) handleSelectPlan(plus);
                    }}
                    disabled={!!selecting || plans.length === 0}
                  >
                    Unlock More <ExternalLink className="w-3.5 h-3.5 ml-1" />
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
